import { useMutation, useQueryClient } from '@tanstack/react-query';
import { apiFetch } from '@/lib/api/client';
import { queryKeys } from '@/lib/query/query-keys';
import type { TripDetailCost, TripDetailCostDocument } from '@/hooks/trips/types';

interface ApiErrorPayload {
  error?: {
    message?: string;
  };
}

function extractErrorMessage(payload: unknown, fallback: string): string {
  if (payload && typeof payload === 'object' && 'error' in (payload as Record<string, unknown>)) {
    const parsed = payload as ApiErrorPayload;
    const message = parsed.error?.message;
    if (typeof message === 'string' && message.trim().length > 0) {
      return message;
    }
  }

  return fallback;
}

type CostType = 'daily' | 'hotel' | 'transportation' | 'fuel' | 'other';

interface CostPayload {
  costType: CostType;
  actualCost: number;
  currencyId: number;
  documentIds: number[];
  comment?: string;
}

interface CreateTripCostVariables {
  tripId: string;
  payload: CostPayload;
}

interface UpdateTripCostVariables {
  costId: number;
  tripId: string;
  payload: CostPayload;
}

interface DeleteTripCostVariables {
  costId: number;
  tripId: string;
}

function mapDocument(record: unknown): TripDetailCostDocument | null {
  if (!record || typeof record !== 'object') {
    return null;
  }

  const docRecord = record as Record<string, unknown>;
  const id = Number(docRecord.id ?? 0);
  const name = typeof docRecord.filename === 'string' ? docRecord.filename : '';
  const mimetype = typeof docRecord.mimetype === 'string' ? docRecord.mimetype : '';
  const size = Number(docRecord.size ?? 0);
  const sizeHuman =
    typeof docRecord.size_human === 'string' && docRecord.size_human.trim().length > 0
      ? docRecord.size_human
      : undefined;

  if (!name || !Number.isFinite(id)) {
    return null;
  }

  return {
    id,
    name,
    mimetype: mimetype || undefined,
    size: Number.isFinite(size) ? size : undefined,
    sizeHuman,
  };
}

function mapCost(record: unknown): TripDetailCost {
  if (!record || typeof record !== 'object') {
    return {
      id: 0,
      costType: '',
      actualCost: 0,
      currencyId: 0,
      currencyName: '',
      documents: [],
    };
  }

  const data = record as Record<string, unknown>;
  const documentsRaw = Array.isArray(data.documents) ? data.documents : [];

  return {
    id: Number(data.id ?? 0),
    costType: String(data.cost_type ?? ''),
    costTypeLabel:
      typeof data.cost_type_label === 'string' ? (data.cost_type_label as string) : undefined,
    actualCost: Number(data.actual_cost ?? 0),
    currencyId: Number(data.currency_id ?? 0),
    currencyName: typeof data.currency_name === 'string' ? (data.currency_name as string) : '',
    documents: documentsRaw
      .map(mapDocument)
      .filter((value): value is TripDetailCostDocument => Boolean(value)),
    comment: typeof data.comment === 'string' ? data.comment : undefined,
  };
}

async function createTripCost({
  tripId,
  payload,
}: CreateTripCostVariables): Promise<TripDetailCost> {
  const requestBody: Record<string, unknown> = {
    cost_type: payload.costType,
    actual_cost: payload.actualCost,
    currency_id: payload.currencyId,
    document_ids: payload.documentIds,
  };

  if (typeof payload.comment === 'string' && payload.comment.trim().length > 0) {
    requestBody.comment = payload.comment;
  }

  const response = await apiFetch(`/api/v1/business-trip-requests/${tripId}/costs`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(requestBody),
  });

  const json = await response.json().catch(() => null);

  if (!response.ok) {
    const message = extractErrorMessage(json, 'Failed to create cost');
    throw new Error(message);
  }

  return mapCost(json);
}

async function updateTripCost({
  costId,
  payload,
}: UpdateTripCostVariables): Promise<TripDetailCost> {
  const requestBody: Record<string, unknown> = {
    cost_type: payload.costType,
    actual_cost: payload.actualCost,
    currency_id: payload.currencyId,
    document_ids: payload.documentIds,
  };

  if (typeof payload.comment === 'string' && payload.comment.trim().length > 0) {
    requestBody.comment = payload.comment;
  }

  const response = await apiFetch(`/api/v1/business-trip-costs/${costId}`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(requestBody),
  });

  const json = await response.json().catch(() => null);

  if (!response.ok) {
    const message = extractErrorMessage(json, 'Failed to update cost');
    throw new Error(message);
  }

  return mapCost(json);
}

async function deleteTripCost({ costId }: DeleteTripCostVariables): Promise<void> {
  const response = await apiFetch(`/api/v1/business-trip-costs/${costId}`, {
    method: 'DELETE',
  });

  if (!response.ok) {
    const payload = await response.json().catch(() => null);
    const message = extractErrorMessage(payload, 'Failed to delete cost');
    throw new Error(message);
  }
}

export function useCreateTripCost() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: createTripCost,
    onSuccess: (_result, variables) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.trips });
      queryClient.invalidateQueries({ queryKey: queryKeys.trip(variables.tripId) });
      queryClient.invalidateQueries({ queryKey: queryKeys.tripCosts(variables.tripId) });
    },
    onError: (error) => {
      console.error('Error creating trip cost:', error);
    },
  });
}

export function useUpdateTripCost() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: updateTripCost,
    onSuccess: (_result, variables) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.trips });
      queryClient.invalidateQueries({ queryKey: queryKeys.trip(variables.tripId) });
      queryClient.invalidateQueries({ queryKey: queryKeys.tripCosts(variables.tripId) });
    },
    onError: (error) => {
      console.error('Error updating trip cost:', error);
    },
  });
}

export function useDeleteTripCost() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: deleteTripCost,
    onSuccess: (_result, variables) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.trips });
      queryClient.invalidateQueries({ queryKey: queryKeys.trip(variables.tripId) });
      queryClient.invalidateQueries({ queryKey: queryKeys.tripCosts(variables.tripId) });
    },
    onError: (error) => {
      console.error('Error deleting trip cost:', error);
    },
  });
}

export type { CostPayload, CostType, TripDetailCostDocument };
